<template>
  <Toolbar class="p-mb-4 p-mt-4 p-p-2">
    <template #start>
      <div style="font-size: 1.4em; color: inherit" class="p-p-2">
        {{ $t('Invoices') }}
      </div>
    </template>
    <template #end>
      <div class="p-d-flex p-ai-center p-mr-2">
        <Checkbox :disabled="dataTableIsLoading || tenantDataIsLoading" @change="unpaidFilterChanged"
                  id="billing-invoices-show-only-unpaid-inputswitch"
                  :binary="true"
                  v-model="showOnlyUnpaid"/>
        <div>
          <label for="billing-invoices-show-only-unpaid-inputswitch">
            <div class="pointer p-p-1">{{ $t('showOnlyUnpaidLabel') }}</div>
          </label>
        </div>
      </div>
      <DatesRangeCalendar :disabled="dataTableIsLoading || tenantDataIsLoading"
                          ref="datesRangeCalendar"
                          :isEmptyByDefault="true"
                          @change-dates-range="changeDatesRange"
                          class="p-mr-2"/>
      <SearchInput :disabled="dataTableIsLoading || tenantDataIsLoading"
                   @change-processed-search-value="changeProcessedSearchValue"
                   v-model="searchData"
                   @show-spinner="isLoading = true"/>
    </template>
  </Toolbar>

  <Spinner v-if="isLoading"/>
  <DesktopDataTable v-else
                    :dataTableIsLoading="dataTableIsLoading || tenantDataIsLoading"
                    :showHeader="false"
                    :itemsArrayIsEmpty="itemsArrayIsEmpty"
                    :sort="sort"
                    :showSearchInput="false"
                    :actionButtonsCount="actionButtonsCount"
                    @sort-items="sortItems"
                    :headers="[
                      {name: 'number', title: $t('Number'), minWidth: '150px', sortable: true},
                      {name: 'company', title: $t('Company'), sortable: false},
                      {name: 'email', title: $t('Billing Email'), sortable: false},
                      {name: 'invoice_date', title: $t('issuedLabel'), sortable: true},
                      {name: 'payment_due', title: $t('paymentDueLabel'), sortable: false},
                      {name: 'overdue', title: '', sortable: false},
                      {name: 'period', title: $t('Period'), sortable: false},
                      {name: 'total', title: $t('Sum'), sortable: false},
                      {name: 'unpaid', title: $t('Due'), sortable: false},
                      {name: 'state', title: $t('Status'), sortable: false},
                      {name: '', title: null, sortable: false},
                      {name: 'created_at', title: $t('Created'), sortable: true},
                  ]">
    <template v-slot:body>
      <tr v-for="(item, index) of items"
          :key="item.id ?? index"
          :class="{'updated-item': isItemUpdated(item.id)}">
        <td>
          <span>{{ item.number }}</span>
        </td>

        <td>
          <span v-if="item.company_name">{{ item.company_name }}</span>
          <i v-else class="ti-minus"/>
        </td>

        <td>
          <span v-if="item.billing_email">{{ item.billing_email }}</span>
          <i v-else class="ti-minus"/>
        </td>

        <td>
          <span v-if="item.invoice_date">{{ formatUtcDay(item.invoice_date) }}</span>
          <i v-else class="ti-minus"/>
        </td>
        <td>
          <span v-if="item.due_at"
                :class="{'warning-color': item.due_at && (countOverdueDays(item.due_at) <= 0) && item.state && +item.unpaid_amount > 0 && [2, 4, 7].includes(+item.state) }">
            {{ formatUtcDay(item.due_at) }}
          </span>
          <i v-else class="ti-minus"/>
        </td>
        <td>
          <span v-if="item.due_at && item.state && +item.unpaid_amount > 0 && [2, 4, 7].includes(+item.state)"
                :class="{'warning-color': countOverdueDays(item.due_at) <= 0 }">
            {{ countOverdueDays(item.due_at) }}
          </span>
        </td>

        <td>
          <span v-if="item.period_start && item.period_end">
            {{ formatUtcDay(item.period_start) }} – {{ formatUtcDay(item.period_end - 1) }}
          </span>
          <span v-else-if="item.period_start">
            {{ formatUtcDay(item.period_start) }} –
          </span>
          <span v-else-if="item.period_end">
            – {{ formatUtcDay(item.period_end - 1) }}
          </span>
          <i v-else class="ti-minus"/>
        </td>

        <td>
          {{ formatBillingCurrency(item.total, item.currency || 'EUR') }}
        </td>

        <td>
           <span
               :class="{
                  'warning-color':
                    item.due_at
                    && countOverdueDays(item.due_at) <= 0
                    && +item.unpaid_amount > 0
                    && [2, 4, 7].includes(+item.state)
                }">
              {{ formatBillingCurrency(item.unpaid_amount, item.currency || 'EUR') }}
            </span>
        </td>

        <td>
          <div  class="p-d-flex p-flex-wrap p-ai-center" v-if="item.state && +item.state">
            <div class="state-badge"
                 :class="BillingInvoiceStates.find(state => state.num === +item.state)?.color || ''">
              {{ BillingInvoiceStates.find(state => state.num === +item.state)?.label[$i18n.locale] || '' }}
            </div>
          </div>
          <i v-else class="ti-minus"/>
        </td>

        <td>
          <DownloadPdfButton :item="item" isSmall type="billing" downloadEndpoint="billing-invoice/download-pdf"/>
        </td>


        <td class="table-date-line">
                <span v-if="item.created_at">
                  {{ formatBy(item.created_at, item.creator) }}
                </span>
          <i v-else class="ti-minus"/>
        </td>
      </tr>
    </template>
  </DesktopDataTable>

  <OwnPaginator v-show="items?.length"
                :rows="pagination.perPage"
                :currentPage="pagination.currentPage - 1"
                :totalPages="pagination.pageCount"
                :totalRecords="pagination.totalCount"
                :rowsPerPageOptions="[5,10,25,50,100]"
                :pageLinkSize="11"/>

  <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>
</template>

<script>
import httpClient from '@/services/http.services'
import BillingInvoiceStates from '@/translations/states/BillingInvoiceStates'
import formatMixins from '@/mixins/formatMixins'
import calculateMixins from '@/mixins/calculateMixins'
import httpMixins from '@/mixins/httpMixins'
import datatableMixins from "@/mixins/datatableMixins";
import settings from "@/settings";
import DatesRangeCalendar from "@/components/DatesRangeCalendar";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import DownloadPdfButton from "@/components/DownloadPdfButton.vue";
import templateLanguageMixin from '@/mixins/templateLanguageMixin'

export default {
  mixins: [ formatMixins, calculateMixins, httpMixins, datatableMixins, showErrorsMixins, templateLanguageMixin ],
  components: {
    DownloadPdfButton,
    DatesRangeCalendar,
  },
  props: {
    tenantDataIsLoading: {
      type: Boolean,
      default: false
    },
  },
  watch: {
    '$route'() {
      const route = this.$route.path.split('/')
      if (
          this.$route.path === '/login'
          || route.indexOf('account') === -1
          || route.indexOf('billing-invoices') === -1
      ) {
        return false
      }

      this.checkRouteQuery()
      this.applyRouteFilters()
      this.applyRouteHighlight()
      this.getItems()
    },
  },
  data() {
    return {
      showOnlyUnpaid: false,
      apiLink: 'billing-invoice',
      routePath: '/billing-invoices',
      sort: {
        column: 'invoice_date',
        direction: '-',
      },
      BillingInvoiceStates,
    }
  },
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    this.checkRouteQuery()
    this.applyRouteFilters()
    this.applyRouteHighlight()
    this.$store.commit('SET_FETCH_IN_FLIGHT', false)
    this.getPageData()
  },
  methods: {
    applyRouteFilters() {
      this.showOnlyUnpaid = String(
          this.$route.query.unpaid || ''
      ) === '1'
    },

    unpaidFilterChanged() {
      this.pagination.currentPage = 1

      const query = {
        ...this.$route.query,
      }

      if (this.showOnlyUnpaid) {
        query.unpaid = '1'
      } else {
        delete query.unpaid
      }

      const currentValue = String(this.$route.query.unpaid || '')
      const nextValue = String(query.unpaid || '')

      if (currentValue === nextValue) {
        this.getItems(true)
        return
      }

      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query,
      })
    },

    applyRouteHighlight() {
      const invoiceId = Number(this.$route.query.highlight)

      this.updatedItemId = Number.isFinite(invoiceId) && invoiceId > 0
          ? invoiceId
          : null
    },
    getPageData() {
      if (!this.userCanGetData || this.fetchInFlight) return

      this.getItems()
    },

    async getItems(showSpinner = false) {
      if (!this.userCanGetData || this.fetchInFlight) return
      if (showSpinner || this.itemNotFound) this.isLoading = true

      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      const ctrl = this.setController('billingInvoices')
      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          method: 'post',
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.isFocusMode ? null : (this.processedSearchValue ?? null),
            expand: this.fieldsExpand,
            show_only_unpaid: this.isFocusMode ? 0 : +this.showOnlyUnpaid,
          },
          data: {
            dateFrom: this.fromTimestamp,
            dateTo: this.toTimestamp,
          },
          signal: ctrl?.signal,
        })
        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }

          if (this.$route.query.show && (data.length === 0)) {
            this.showItemNotFoundError()
            this.itemNotFound = true
            return
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.items = []
        this.showError?.(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        if (this._abortors?.billingInvoices === ctrl) {
          this._abortors.billingInvoices = null
        }
      }
    },
    countOverdueDays(paymentDueDay) {
      if (!paymentDueDay) return null

      const now = new Date()
      const due = new Date(Number(paymentDueDay) * 1000)

      const todayUtc = Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate()
      )

      const dueUtc = Date.UTC(
          due.getUTCFullYear(),
          due.getUTCMonth(),
          due.getUTCDate()
      )

      return Math.round((dueUtc - todayUtc) / 86400000)
    },
  },
  computed: {
    actionButtonsCount() {
      return 0
    },
  }
}
</script>

<style scoped lang="scss">
td {
  min-height: 44px;
}
</style>